import React, { useState, useEffect, useCallback } from 'react';
import {
    Table,
    Button,
    Badge,
    Card,
    Row,
    Col,
    Form,
    ButtonGroup,
    Modal,
    Alert,
    Spinner
} from 'react-bootstrap';
import {
    FaFileAlt,
    FaDownload,
    FaTrash,
    FaExclamationTriangle,
    FaUpload,
    FaCheckCircle,
    FaTimesCircle
} from 'react-icons/fa';
import api from '../services/api';
import useOCR from '../hooks/useOCR';


// ✅ REQUIRED DOCUMENTS FOR VEHICLE
const REQUIRED_VEHICLE_DOCUMENTS = [
    { category: 'ruhsat', name: 'Ruhsat', required: true },
    { category: 'alis_sozlesmesi', name: 'Alış Sözleşmesi', required: true },
    { category: 'satis_sozlesmesi', name: 'Satış Sözleşmesi', required: false },
    { category: 'trafik_sigortasi', name: 'Trafik Sigortası Poliçesi', required: false },
    { category: 'sigorta_iptal_policesi', name: 'Sigorta İptal Poliçesi', required: false },
    { category: 'alis_faturasi', name: 'Alış Faturası', required: false },
    { category: 'satis_faturasi', name: 'Satış Faturası', required: false },
    { category: 'ekspertiz_raporu', name: 'Ekspertiz Raporu', required: false }
];

const OCR_ENABLED_CATEGORIES = ['ruhsat', 'alis_sozlesmesi', 'satis_sozlesmesi', 'alis_faturasi'];


const DocumentViewer = ({
    reportId = null,
    customerId = null,
    vehicleId = null,
    customerInfo = null,
    showActions = true,
    isAdmin = false,
    currentUserId = null,
    onDocumentUpdate = () => { },
    onUploadSuccess = null,
    onOCRDataExtracted = null // ✅ YENİ: OCR callback
}) => {
    // State
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({
        category: '',
        status: ''
    });

    const [categories, setCategories] = useState([]);



    // Modal states - SADECE DELETE MODAL
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);

    // ✅ OCR Hook - Vehicle context only
    const { processDocument, processing: ocrProcessing } = useOCR();

    const fetchCategories = async () => {
        try {
            const response = await api.get('/documents/categories');
            const allCategories = response.data.categories || [];

            // "Diger" kategorisi yoksa ekle
            if (!allCategories.find(cat => cat.value === 'diger')) {
                allCategories.push({ value: 'diger', label: 'Diğer' });
            }

            setCategories(allCategories);
        } catch (error) {
            console.error('Kategoriler yüklenemedi:', error);
        }
    };

    const fetchDocuments = useCallback(async () => {
        console.log('🔍 fetchDocuments called with:', { reportId, customerId, vehicleId, filters });

        setLoading(true);
        try {
            let url = '/documents';
            const params = new URLSearchParams();

            if (filters.category) params.append('category', filters.category);
            if (filters.status) params.append('status', filters.status);

            if (vehicleId) {
                url = `/documents/vehicle/${vehicleId}`;
                console.log('🔍 DEBUG - Using vehicle endpoint for vehicleId:', vehicleId);
            } else if (reportId) {
                url = `/documents/report/${reportId}`;
                console.log('🔍 DEBUG - Using report endpoint for reportId:', reportId);
            } else if (customerId) {
                url = `/documents/customer/${customerId}`;
                console.log('🔍 DEBUG - Using customer endpoint for customerId:', customerId);
            }

            if (params.toString()) {
                url += `?${params.toString()}`;
            }

            console.log('🔍 DEBUG - Final API URL:', url);

            const response = await api.get(url);
            console.log('📄 DEBUG - API Response count:', response.data.documents?.length);
            setDocuments(response.data.documents || []);
        } catch (error) {
            console.error('❌ DEBUG - Error fetching documents:', error);
        } finally {
            setLoading(false);
        }
    }, [reportId, customerId, vehicleId, filters]);

    useEffect(() => {
        fetchDocuments();
        fetchCategories();
    }, [reportId, customerId, vehicleId, filters]);

    useEffect(() => {
        const interval = setInterval(() => {
            if (!actionLoading && !loading) {
                fetchDocuments();
            }
        }, 30000);

        return () => clearInterval(interval);
    }, [actionLoading, loading, fetchDocuments]);

    // ✅ SIMPLE UPLOAD HANDLER - NO MODAL

    // ✅ FINAL FIX: DocumentViewer.js'teki handleSimpleUpload fonksiyonu
    // ✅ DocumentViewer.js - SADECE handleSimpleUpload fonksiyonunu değiştir
    // API service'e dokunma!

    const handleSimpleUpload = async (event, category) => {
        const file = event.target.files[0];
        if (!file) return;

        const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
        if (!allowedTypes.includes(file.type)) {
            alert('Sadece PDF, JPG, PNG dosyaları yüklenebilir.');
            return;
        }

        setActionLoading(true);
        try {
            const formData = new FormData();
            formData.append('document', file);
            formData.append('category', category);
            formData.append('title', file.name);

            // ID alanlarını gönder
            if (vehicleId) formData.append('vehicleId', vehicleId);
            if (reportId) formData.append('reportId', reportId);
            if (customerId) formData.append('customerId', customerId);

            // FormData debug
            console.log('📄 FormData contents:');
            for (const [key, value] of formData.entries()) {
                if (value instanceof File) {
                    console.log(key, `File: ${value.name} (${value.size} bytes, ${value.type})`);
                } else {
                    console.log(key, value);
                }
            }

            // ✅ FETCH İLE UPLOAD - API service'e dokunmuyoruz
            console.log('🚀 Starting fetch upload...');

            const token = localStorage.getItem('token');
            const response = await fetch('/api/documents/upload', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    // Content-Type'ı ASLA set etme - fetch otomatik boundary ekleyecek
                },
                body: formData
            });

            console.log('📊 Fetch response status:', response.status);

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();
            console.log('✅ Upload response:', result);

            if (result.success) {
                fetchDocuments();
                onDocumentUpdate && onDocumentUpdate(result.document);

                // ✅ OCR Processing - Vehicle specific categories only
                const shouldEnableOCR = vehicleId && OCR_ENABLED_CATEGORIES.includes(category);

                if (shouldEnableOCR && onOCRDataExtracted) {
                    console.log(`🔍 OCR processing for category: ${category}`);

                    try {
                      
                        const ocrResult = await processDocument(file, category);

                        if (ocrResult.success && ocrResult.mappedData) {
                            // Create proper data structure for callback
                            const extractedData = { [ocrResult.documentType]: ocrResult.mappedData };

                            // Generate mapped fields from result
                            let mappedFields = [];
                            if (ocrResult.mappedData) {
                                Object.keys(ocrResult.mappedData).forEach(key => {
                                    if (key === 'purchasedFrom' || key === 'soldTo' || key === 'technicalDetails') {
                                        // Handle nested objects
                                        Object.keys(ocrResult.mappedData[key]).forEach(subKey => {
                                            mappedFields.push(`${key}.${subKey}`);
                                        });
                                    } else {
                                        // Handle direct fields
                                        mappedFields.push(key);
                                    }
                                });
                            }

                            // Trigger callback
                            onOCRDataExtracted(extractedData, mappedFields);

                            alert(`✅ Belge yüklendi ve OCR tamamlandı!\n${mappedFields.length} alan otomatik dolduruldu.`);
                        } else {
                            alert('Belge başarıyla yüklendi ancak OCR verileri çıkarılamadı.');
                        }
                    } catch (ocrError) {
                        console.error('❌ OCR processing failed:', ocrError);
                        alert('Belge yüklendi ancak OCR başarısız. Manuel kontrol edin.');
                    }
                } else {
                    alert('Belge başarıyla yüklendi!');
                }
            } else {
                throw new Error(result.message || 'Upload failed');
            }
        } catch (error) {
            console.error('❌ Upload error:', error);
            alert('Hata: ' + error.message);
        } finally {
            setActionLoading(false);
            event.target.value = '';
        }
    };

    const handleDownload = async (document) => {
        try {
            const response = await fetch(document.file.cloudinaryUrl);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = window.document.createElement('a');
            a.href = url;

            // ✅ EXTENSION'I KORU!
            a.download = document.file.originalName || document.originalName || 'document.pdf';

            window.document.body.appendChild(a);
            a.click();
            window.document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Download error:', error);
        }
    };

    const handleDelete = async () => {
        if (!selectedDocument) return;

        setActionLoading(true);
        try {
            const response = await api.delete(`/documents/${selectedDocument._id}`);

            if (response.data.success) {
                await fetchDocuments();
                onDocumentUpdate(null);
                setShowDeleteModal(false);
                alert('Belge başarıyla silindi!');
            }
        } catch (error) {
            console.error('Delete error:', error);
            alert('Belge silinirken hata oluştu');
        } finally {
            setActionLoading(false);
        }
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            'active': { variant: 'success', text: 'Aktif' },
            'archived': { variant: 'secondary', text: 'Arşivlenmiş' },
        };

        const config = statusConfig[status] || { variant: 'secondary', text: status };
        return <Badge bg={config.variant}>{config.text}</Badge>;
    };

    const getFileIcon = (mimeType) => {
        if (mimeType === 'application/pdf') {
            return <FaFileAlt className="text-danger" />;
        } else if (mimeType.startsWith('image/')) {
            return <FaFileAlt className="text-success" />;
        }
        return <FaFileAlt className="text-secondary" />;
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const getCategoryName = (categoryValue) => {
        const category = categories.find(cat => cat.value === categoryValue);
        return category ? category.label : categoryValue;
    };

    const canDeleteDocument = (document) => {
        return isAdmin || (currentUserId && document.uploadedBy?._id === currentUserId);
    };

    // ✅ Check required documents for vehicle
    const getRequiredDocumentsStatus = () => {
        if (!vehicleId) return [];

        return REQUIRED_VEHICLE_DOCUMENTS.map(req => {
            const hasDocument = documents.some(doc => doc.category === req.category);
            const document = documents.find(doc => doc.category === req.category);

            return {
                ...req,
                hasDocument,
                document
            };
        });
    };

    const requiredDocsStatus = getRequiredDocumentsStatus();
    const missingRequiredDocs = requiredDocsStatus.filter(req => req.required && !req.hasDocument);

    return (
        <div>
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">
                    <FaFileAlt className="me-2" />
                    Belgeler ({documents.length} belge)
                    {vehicleId && missingRequiredDocs.length > 0 && (
                        <Badge bg="danger" className="ms-2">
                            {missingRequiredDocs.length} eksik zorunlu belge
                        </Badge>
                    )}
                </h5>
            </div>

            {/* ✅ Required Documents Status for Vehicle */}
            {vehicleId && requiredDocsStatus.length > 0 && (
                <Card className="mb-3">
                    <Card.Header>
                        <h6 className="mb-0">📋 Zorunlu Belgeler Durumu</h6>
                    </Card.Header>
                    <Card.Body>
                        <Row className="g-2">
                            {requiredDocsStatus.map(req => (
                                <Col md={6} lg={4} key={req.category}>
                                    <Card
                                        className={`border-2 h-100 ${req.hasDocument
                                            ? 'border-success'
                                            : req.required
                                                ? 'border-danger'
                                                : 'border-warning'
                                            }`}
                                    >
                                        <Card.Body className="p-3">
                                            <div className="d-flex align-items-center justify-content-between mb-2">
                                                <div className="d-flex align-items-center">
                                                    {req.hasDocument ? (
                                                        <FaCheckCircle className="text-success me-2" />
                                                    ) : (
                                                        <FaTimesCircle className={`me-2 ${req.required ? 'text-danger' : 'text-warning'}`} />
                                                    )}
                                                    <h6 className="mb-0 small">{req.name}</h6>
                                                </div>
                                                <Badge bg={req.hasDocument ? 'success' : req.required ? 'danger' : 'warning'} className="small">
                                                    {req.hasDocument ? '✓' : '✗'}
                                                </Badge>
                                            </div>

                                            {req.hasDocument && req.document && (
                                                <div className="mb-2">
                                                    <small className="text-muted d-block">
                                                        📄 {req.document.file?.originalName || req.document.title}
                                                    </small>
                                                    <small className="text-muted">
                                                        {new Date(req.document.createdAt).toLocaleDateString('tr-TR')}
                                                    </small>
                                                </div>
                                            )}

                                            {showActions && (
                                                <div>
                                                    {/* ✅ SIMPLE FILE UPLOAD - NO MODAL */}
                                                    <input
                                                        type="file"
                                                        id={`upload-${req.category}`}
                                                        accept=".pdf,.jpg,.jpeg,.png"
                                                        style={{ display: 'none' }}
                                                        onChange={(e) => handleSimpleUpload(e, req.category)}
                                                        disabled={loading || actionLoading}
                                                    />
                                                    <Button
                                                        variant={req.hasDocument ? 'outline-primary' : req.required ? 'danger' : 'warning'}
                                                        size="sm"
                                                        onClick={() => document.getElementById(`upload-${req.category}`).click()}
                                                        className="w-100"
                                                        disabled={loading || actionLoading}
                                                    >
                                                        {actionLoading ? (
                                                            <Spinner size="sm" animation="border" />
                                                        ) : req.hasDocument ? '🔄 Değiştir' : '📎 Yükle'}
                                                    </Button>
                                                </div>
                                            )}

                                            {req.required && !req.hasDocument && (
                                                <small className="text-danger d-block mt-1">
                                                    ⚠️ Zorunlu belge
                                                </small>
                                            )}
                                        </Card.Body>
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                    </Card.Body>
                </Card>
            )}

            {/* Filters */}
            <Card className="mb-3">
                <Card.Body className="py-2">
                    <Row className="align-items-center">
                        <Col md={4}>
                            <Form.Select
                                size="sm"
                                value={filters.category}
                                onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value }))}
                                disabled={loading}
                            >
                                <option value="">Diğer Kategoriler</option>  {/* ✅ Default text değiştir */}
                                {categories.map(cat => (
                                    <option key={cat.value} value={cat.value}>
                                        {cat.label}
                                    </option>
                                ))}
                            </Form.Select>
                        </Col>
                        <Col md={4}>
                            <Form.Select
                                size="sm"
                                value={filters.status}
                                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                                disabled={loading}
                            >
                                <option value="">Tüm Durumlar</option>
                                <option value="active">Aktif</option>
                                <option value="archived">Arşivlenmiş</option>
                            </Form.Select>
                        </Col>
                        <Col md={4} className="text-end">
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={fetchDocuments}
                                disabled={loading}
                                className="me-2"
                            >
                                {loading ? (
                                    <Spinner size="sm" animation="border" />
                                ) : (
                                    'Yenile'
                                )}
                            </Button>
                            <small className="text-muted">
                                {documents.length} belge
                            </small>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Documents Table */}
            <Card>
                <Card.Body className="p-0">
                    {loading ? (
                        <div className="text-center py-4">
                            <Spinner animation="border" />
                            <div className="mt-2">Belgeler yükleniyor...</div>
                        </div>
                    ) : documents.length === 0 ? (
                        <div className="text-center py-4">
                            <FaFileAlt size={48} className="text-muted mb-3" />
                            <h5>Henüz belge yüklenmemiş</h5>
                            <p className="text-muted mb-3">
                                Bu {vehicleId ? 'araç' : reportId ? 'rapor' : 'müşteri'} için henüz belge bulunmuyor.
                            </p>
                            {showActions && (
                                <div>
                                    <Button
                                        variant="primary"
                                        onClick={() => document.getElementById('general-upload').click()}
                                    >
                                        <FaUpload className="me-2" />
                                        İlk Belgeyi Yükle
                                    </Button>

                                    {/* Hidden general upload */}
                                    <input
                                        type="file"
                                        id="general-upload"
                                        accept=".pdf,.jpg,.jpeg,.png"
                                        style={{ display: 'none' }}
                                        onChange={(e) => handleSimpleUpload(e, 'diger')}
                                    />
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <Table hover className="mb-0">
                                <thead>
                                    <tr>
                                        <th>Belge</th>
                                        <th>Kategori</th>
                                        <th>Durum</th>
                                        <th>Yüklenme</th>
                                        {showActions && <th>İşlemler</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {documents.map(doc => (
                                        <tr key={doc._id}>
                                            <td>
                                                <div className="d-flex align-items-center">
                                                    {getFileIcon(doc.file.mimeType)}
                                                    <div className="ms-2">
                                                        <div className="fw-bold">{doc.title}</div>
                                                        <small className="text-muted">
                                                            {formatFileSize(doc.file.size)}
                                                            {doc.file.mimeType === 'application/pdf' && (
                                                                <Badge bg="danger" className="ms-1">PDF</Badge>
                                                            )}
                                                            {doc.file.mimeType.startsWith('image/') && (
                                                                <Badge bg="success" className="ms-1">Resim</Badge>
                                                            )}
                                                        </small>
                                                        {doc.notes && (
                                                            <div className="small text-muted mt-1">
                                                                {doc.notes}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <div>{getCategoryName(doc.category)}</div>
                                                {doc.expiryDate && (
                                                    <small className="text-muted">
                                                        Son: {new Date(doc.expiryDate).toLocaleDateString('tr-TR')}
                                                    </small>
                                                )}
                                            </td>
                                            <td>
                                                {getStatusBadge(doc.status)}
                                            </td>
                                            <td>
                                                <div>{new Date(doc.createdAt).toLocaleDateString('tr-TR')}</div>
                                                <small className="text-muted">
                                                    {doc.uploadedBy?.name || 'Bilinmeyen'}
                                                </small>
                                            </td>
                                            {showActions && (
                                                <td>
                                                    <ButtonGroup size="sm">
                                                        <Button
                                                            variant="outline-primary"
                                                            onClick={() => handleDownload(doc)}
                                                            title="İndir/Görüntüle"
                                                        >
                                                            <FaDownload />
                                                        </Button>

                                                        {canDeleteDocument(doc) && (
                                                            <Button
                                                                variant="outline-danger"
                                                                onClick={() => {
                                                                    setSelectedDocument(doc);
                                                                    setShowDeleteModal(true);
                                                                }}
                                                                title="Sil"
                                                                disabled={actionLoading}
                                                            >
                                                                <FaTrash />
                                                            </Button>
                                                        )}
                                                    </ButtonGroup>
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </Card.Body>
            </Card>

            {/* Delete Modal */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Belgeyi Sil</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Alert variant="warning">
                        <FaExclamationTriangle className="me-2" />
                        Bu işlem geri alınamaz!
                    </Alert>
                    <p>
                        <strong>{selectedDocument?.title}</strong> belgesini kalıcı olarak silmek istediğinizden emin misiniz?
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setShowDeleteModal(false)}
                        disabled={actionLoading}
                    >
                        İptal
                    </Button>
                    <Button
                        variant="danger"
                        onClick={handleDelete}
                        disabled={actionLoading}
                    >
                        {actionLoading ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" />
                                Siliniyor...
                            </>
                        ) : (
                            <>
                                <FaTrash className="me-2" />
                                Sil
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default DocumentViewer;